import React from 'react';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import FAIcon from '#/common/components/FAIcon';
import { isLightTheme } from '#/common/utils/themeDetection';

const getBorderColor = (isDragging: boolean, disabled: boolean, theme: any) => {
  if (isDragging && !disabled) {
    return color('ui', 'dark2');
  }
  if (isLightTheme(theme)) {
    return color('ui', 'middle');
  }
  return color('ui', 'dark');
};

const getBackgroundColor = (isDragging: boolean, disabled: boolean, theme: any) => {
  if (isDragging && !disabled) {
    return isLightTheme(theme) ? color('ui', 'light2') : color('ui', 'dark');
  }
  return color('custom', 'formGroupBackground');
};

const getHoverBackgroundColor = (theme: any) => {
  return isLightTheme(theme) ? color('ui', 'light2') : color('ui', 'dark');
};

const DropZoneStyles = styled.div<{ isDragging: boolean; disabled: boolean }>`
  border: 2px dashed
    ${({ isDragging, disabled, theme }) => getBorderColor(isDragging, disabled, theme)};
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  background-color: ${({ isDragging, disabled, theme }) =>
    getBackgroundColor(isDragging, disabled, theme)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.8 : 1)};
  transition: all 0.3s ease;

  &:hover {
    ${({ disabled }) => (disabled ? '' : `border-color: ${color('ui', 'dark2')};`)}
    ${({ disabled, theme }) =>
      disabled ? '' : `background-color: ${getHoverBackgroundColor(theme)};`}
  }

  .spin-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    animation: anim-spin 1.5s linear infinite;
  }

  @keyframes anim-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export interface DropZoneProps {
  displayFormats: string;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onClick: () => void;
  loading?: boolean;
}

const DropZone: React.FC<DropZoneProps> = ({
  displayFormats,
  onDrop,
  onClick,
  loading = false,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (loading) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (loading) return;
    event.preventDefault();
    event.stopPropagation();
    // Windows Chrome shows a "not allowed" cursor unless dropEffect is set explicitly
    event.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (loading) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (loading) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    onDrop(event);
  };

  const handleClick = () => {
    if (loading) return;
    onClick();
  };

  return (
    <DropZoneStyles
      disabled={loading}
      isDragging={isDragging}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}>
      <NuvoBox display="flex" flexDirection="column" alignItems="center" gap={16}>
        <span className={loading ? 'spin-icon' : undefined}>
          <FAIcon
            icon={loading ? 'spinner' : 'up-from-bracket'}
            size="2xl"
            color={color('primary', 'main')}
          />
        </span>
        <NuvoText type="header3">
          {loading ? 'Loading attachments...' : `Drop your ${displayFormats} file here`}
        </NuvoText>
        {!loading && <NuvoLabel>or click to choose what to upload</NuvoLabel>}
      </NuvoBox>
    </DropZoneStyles>
  );
};

export default React.memo(DropZone);
